import { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { Button } from "../../components/Button";
import Avatar from "@mui/material/Avatar";
import { DownloadResourcesToExcel, searchResources } from "../../services/api/resource/resourceService";
import { IGrid } from "../../@types/grid.type";
import { IResourceGridItem } from "../../@types/resource-grid-item.type";
import addCircleImage from "./../../assets/_AddCircleIcon_.svg";
import { stringAvatar } from "../../utils/avatar";
import { useNavigate } from "react-router-dom";
import usePermission from "../../hooks/usePermission";
import { FeatureStatic } from "../../@types/statics/feature.static";
import { getAllResourceCustomField } from "../../services/api/resourceCustomField/resourceCustomFieldService";
import { ICustomField } from "../../@types/customField.type";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../themeGrid";
import { localeText } from "../../localeTextFromGrid";
import { ColDef, FilterChangedEvent, IGetRowsParams } from "ag-grid-community";
import styles from "./styles.module.scss";
import { IResourceDownload, IResourceFilter } from "../../@types/resource-filter.type";
import Box from "@mui/material/Box/Box";
import Tabs from "@mui/material/Tabs/Tabs";
import Tab from "@mui/material/Tab/Tab";
import { SkillsResources } from "./Skills";
import iconExcel from "./../../assets/icon-excel.svg";
import { useSyncActive } from "../../utils/useSyncActive";


const formatBoolean = (value: boolean | undefined): string =>
  value ? "Sim" : "Não";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0, mt: 2 }}>{children}</Box>}
    </div>
  );
}

const columnsDefault: ColDef[] = [
  {
    field: "name",
    headerName: "Colaborador",
    flex: 1,
    minWidth: 400,
    cellRenderer: (params: CustomCellRendererProps) => (
      <>
        <Avatar
          style={{ margin: "5px", opacity: "0.5", float: "left" }}
          {...stringAvatar(params.value)}
        />
        {params.value}
      </>
    ),
  },
  {
    field: "areaName",
    headerName: "Área",
    flex: 1,
    minWidth: 300,
  },
  {
    field: "managerName",
    headerName: "Gestor",
    minWidth: 300,
    flex: 2,
  },
  {
    field: "costCenterName",
    headerName: "Centro de Custo",
    minWidth: 300,
    flex: 2,
  },

  {
    field: "dismissed",
    headerName: "Desligado",
    minWidth: 200,
    flex: 1,
    cellRenderer: (params: CustomCellRendererProps) => (
      <span>{formatBoolean(params.value)}</span>
    ),
  },
  {
    field: "isAllocatable",
    headerName: "Permitir Alocação",
    minWidth: 200,
    flex: 1,
    cellRenderer: (params: CustomCellRendererProps) => (
      <span>{formatBoolean(params.value)}</span>
    ),
  },
];

export const Resources: React.FC = () => {
  const [columns, setColumns] = useState(columnsDefault);
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [tabValue, setTabValue] = useState(0);

  const isSyncActive = useSyncActive();

  const permissions = usePermission(FeatureStatic.resources.id);
  let filters = {} as IResourceDownload;

  const navigate = useNavigate();

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersResources")
  );
  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortResources")
  );

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersResources", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortResources", sortState);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const gridApi = gridRef.current.api;

    if (gridApi) {
      // Restaurar filtros
      if (cachedFilters) {
        gridApi.setFilterModel(cachedFilters);
      }

      // Restaurar ordenação
      if (cachedSort) {
        gridApi.applyColumnState({
          state: cachedSort,
          defaultState: { sort: null },
        });
      }
    }
  }, [cachedFilters, cachedSort, columns]);

  function mapCustomFieldsToTheCollumnGrid(
    customFields: ICustomField[]
  ): ColDef[] {
    return customFields
      ?.filter((item) => item.showInList)
      .map((item) => ({
        id: item.id,
        field: item.name,
        headerName: item.name,
        flex: 1,
        minWidth: 200,
        filter: false,
        sortable: false,
      }));
  }
  function mapCustomFieldsToTheRowGrid(
    customers: IResourceGridItem[]
  ): IResourceGridItem[] {
    return customers.map((customer) => {
      const customFieldsObject = Array.isArray(customer.customFields)
        ? Object.fromEntries(
            customer.customFields.map(({ key, value }) => [key, value])
          )
        : {};

      return {
        ...customer,
        ...customFieldsObject,
        customFields: undefined,
      };
    });
  }

  const fetchAllResourceCustomField = async () => {
    try {
      setLoading(true);

      const responseCollumms = await getAllResourceCustomField();
      const mappedCustomColumns = mapCustomFieldsToTheCollumnGrid(
        responseCollumms.result
      );

      setColumns([...columns, ...mappedCustomColumns]);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
    }
    fetchAllResourceCustomField();
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      console.log(params);

      const sortModel = params.sortModel;
      const filterModel = params.filterModel;

      const filter = {
        skip: params.startRow, // Linha inicial para paginação
        take: params.endRow - params.startRow, // Quantidade de linhas
      } as IResourceFilter;

      if (sortModel.length > 0) {
        filter.orderByField = sortModel[0].colId;
        filter.orderByDirection = sortModel[0].sort == "asc" ? 0 : 1;
      }

      if (filterModel.name) {
        filter.name = filterModel.name.filter;
        filters.name = filterModel.name.filter;
      }else{
        filters.name = undefined;
      }


      if (filterModel.areaName) {
        filter.areaName = filterModel.areaName.filter;
        filters.areaName = filterModel.areaName.filter;
      }
      else{
        filters.areaName = undefined;
      }


      if (filterModel.managerName) {
        filter.managerName = filterModel.managerName.filter;
        filters.managerName = filterModel.managerName.filter;
      }else{
        filters.managerName = undefined;
      }


      if (filterModel.costCenterName) {
        filter.costCenterName = filterModel.costCenterName.filter;
        filters.costCenterName = filterModel.costCenterName.filter;
      }else{
        filters.costCenterName = undefined;
      }


      if (filterModel.dismissed) {
        filter.dismissed =
          String(filterModel.dismissed.filter).toLowerCase() == "sim";

        filters.dismissed =
          String(filterModel.dismissed.filter).toLowerCase() == "sim";
      }else{
        filters.dismissed = undefined;
      }


      if (filterModel.isAllocatable) {
        filter.isAllocatable =
          String(filterModel.isAllocatable.filter).toLowerCase() == "sim";

        filters.isAllocatable =
          String(filterModel.isAllocatable.filter).toLowerCase() == "sim";
      }else{
        filters.isAllocatable = undefined;
      }


      try {
        const response = (await searchResources(
          filter
        )) as IGrid<IResourceGridItem>;

        const customerWithCustomizedFields = mapCustomFieldsToTheRowGrid(
          response.items
        );

        params.successCallback(
          customerWithCustomizedFields,
          response.totalRows
        );
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  async function ExportToExcel(filters: IResourceDownload) {
    await DownloadResourcesToExcel(filters);
  }


  return (
    <>
      <main className={styles.resourcesStyle}>
          <div className={styles.titleAndBtnCreate}>
            <h1>Colaboradores</h1>
            {
              tabValue == 0 && !isSyncActive &&
              <div className={styles.btnsFilterAndCreate}>
                <Button
                  iconUrl={addCircleImage}
                  onClick={() => navigate("/resources/create")}
                  content="Novo Colaborador"
                  className={styles.newProjectButton}
                />
              </div>
            }
          </div>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            aria-label="basic tabs example"
            classes={{
              indicator: styles["MuiTabs-indicator"], // Usando colchetes para o nome da classe
            }}
          >
            <Tab label="Lista" {...a11yProps(0)} className={styles.tab} />
            <Tab label="Busca por Colaborador" {...a11yProps(1)} className={styles.tab} />
            {
              tabValue == 0 && 
              <Box sx={{marginLeft: "auto", display: "flex"}}>
                <Button
                  onClick={() => {
                    ExportToExcel(filters);
                  }}
                  iconUrl={iconExcel}
                  className={styles.btnExcel}
                />
              </Box>
            }
          </Tabs>
        </Box>

        <CustomTabPanel value={tabValue} index={0}>
          <div className={`${styles.dataGridContainer}`}>
            <AgGridReact
              ref={gridRef}
              theme={themeGrid}
              localeText={localeText}
              loading={loading}
              columnDefs={columns}
              overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
              defaultColDef={{
                sortable: true,
                editable: false,
                filter: true,
                floatingFilter: true,
              }}
              suppressCellFocus={true}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(row) =>
                permissions.canEdit()
                  ? navigateToUpdatePage(row.data.id)
                  : undefined
              }
              cacheBlockSize={10}
              onFilterChanged={onFilterChanged}
              onSortChanged={onSortChanged}
              rowModelType="infinite"
              datasource={getDataSource()}
              animateRows={true}
              rowHeight={50}
            />
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <SkillsResources />
        </CustomTabPanel>

      </main>
    </>
  );
};
